import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import {faqData  } from './faqData'; // Adjust the import path as necessary


interface FAQModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FAQModal: React.FC<FAQModalProps> = ({ isOpen, onClose }) => {
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');



  const categories = ['all', ...Array.from(new Set(faqData.map(item => item.category)))];

  const filteredFAQs = selectedCategory === 'all' 
    ? faqData 
    : faqData.filter(item => item.category === selectedCategory);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const formatAnswer = (answer: string) => {
    return answer.split('\n').map((line, index) => (
      <span key={index}>
        {line}
        {index < answer.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2 sm:p-4 lg:p-6">
      <div className="bg-white rounded-lg sm:rounded-xl w-full max-w-xs sm:max-w-lg md:max-w-2xl lg:max-w-4xl xl:max-w-6xl h-full sm:h-auto sm:max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-4 sm:py-6 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-black mb-1 sm:mb-2">
                Frequently Asked Questions
              </h2>
              <p className="text-xs sm:text-sm text-neutral-600">
                Find answers to common questions about Cadence IO
              </p>
            </div>
            <button
              onClick={onClose}
              className="ml-4 p-2 hover:bg-neutral-100 rounded-full transition-colors flex-shrink-0"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral-100 bg-neutral-50">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-primary-700 text-white'
                    : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'
                }`}
              >
                {category === 'all' ? 'All Categories' : category}
              </button>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="space-y-3 sm:space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div key={index} className="border border-neutral-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 text-left bg-white hover:bg-neutral-50 transition-colors flex items-center justify-between"
                >
                  <div className="flex-1 pr-4">
                    <h3 className="text-xs sm:text-sm lg:text-base font-medium text-black">
                      {faq.question}
                    </h3>
                    {selectedCategory === 'all' && (
                      <span className="inline-block mt-1 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                        {faq.category}
                      </span>
                    )}
                  </div>
                  {openItems.includes(index) ? (
                    <ChevronUpIcon className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-400 flex-shrink-0" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-400 flex-shrink-0" />
                  )}
                </button>
                
                {openItems.includes(index) && (
                  <div className="px-4 sm:px-6 py-3 sm:py-4 bg-neutral-50 border-t border-neutral-100">
                    <p className="text-xs sm:text-sm lg:text-base text-neutral-700 leading-relaxed">
                      {formatAnswer(faq.answer)}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredFAQs.length === 0 && (
            <div className="text-center py-8">
              <p className="text-neutral-500">No FAQs found for the selected category.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-4 sm:py-5 border-t border-neutral-200 bg-neutral-50 rounded-b-lg sm:rounded-b-xl">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-xs sm:text-sm text-neutral-600 text-center sm:text-left">
              Still have questions? Contact our support team at{' '}
              <a href="mailto:admin@cadenceio.com" className="text-primary-700 hover:text-primary-800 underline">
                admin@cadenceio.com
              </a>
            </p>
            <Button 
              type="button" 
              className="w-full sm:w-auto sm:px-6 sm:text-base"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQModal;